import React, { useEffect, useRef } from 'react';
interface WaveVisualizerProps {
  frequency: number;
  frequencies?: number[];
  colorRGB: string;
  secondaryColorRGB?: string;
  isActive: boolean;
  gyroTilt?: number;
}
export function WaveVisualizer({
  frequency,
  frequencies,
  colorRGB,
  secondaryColorRGB = '255, 255, 255',
  isActive,
  gyroTilt = 0
}: WaveVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const timeRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isActive) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const resize = () => {
      canvas.width = canvas.offsetWidth * (window.devicePixelRatio || 1);
      canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    };
    resize();
    window.addEventListener('resize', resize);
    const displayWidth = canvas.offsetWidth;
    const displayHeight = canvas.offsetHeight;
    const drawWaveGroup = (centerY: number, t: number, mode: number, gyroInfluence: number, waveColorRGB: string, freq: number) => {
      // Draw 3 layered waves with decreasing opacity
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        // Consistent base spatial frequency — enough cycles to look smooth
        const spatialFreq = 0.008 + i * 0.001;
        // Gentle amplitude scaling per layer
        const baseAmp = 40 + Math.abs(gyroInfluence) * 0.5;
        // Consistent, smooth time speed
        const speed = t;
        if (mode > 0) {
          // PREMIUM dual wave: complex intertwined shape
          for (let x = 0; x <= displayWidth; x += 1) {
            const y = Math.sin(x * spatialFreq * 1.2 + speed + i * 0.8) * (baseAmp + i * 15) + Math.cos(x * spatialFreq * 2.5 + speed * 1.3 + i * 0.4) * (baseAmp * 0.4);
            if (x === 0) {
              ctx.moveTo(x, centerY + y);
            } else {
              ctx.lineTo(x, centerY + y);
            }
          }
        } else {
          // STANDARD single wave: each frequency has a distinct visual character
          // but all use smooth, continuous math with small layer offsets
          for (let x = 0; x <= displayWidth; x += 1) {
            let y = 0;
            if (freq === 396) {
              // Deep, slow, powerful wave — grounding energy
              const amp = baseAmp + i * 18;
              y = Math.sin(x * spatialFreq * 0.9 + speed * 0.7 + i * 0.6) * amp + Math.sin(x * spatialFreq * 1.8 + speed * 0.5 + i * 0.3) * (amp * 0.2);
            } else if (freq === 417) {
              // Flowing, organic wave — cleansing energy
              const amp = baseAmp + i * 15;
              y = Math.sin(x * spatialFreq * 1.1 + speed * 0.85 + i * 0.7) * amp + Math.cos(x * spatialFreq * 2.0 + speed * 0.6 + i * 0.5) * (amp * 0.3);
            } else if (freq === 432) {
              // Balanced, natural wave — harmony
              const amp = baseAmp + i * 16;
              y = Math.sin(x * spatialFreq * 1.0 + speed * 0.8 + i * 0.65) * amp + Math.sin(x * spatialFreq * 2.2 + speed * 0.55 + i * 0.4) * (amp * 0.25);
            } else if (freq === 528) {
              // Bright, lively wave — love/repair frequency
              const amp = baseAmp + i * 14;
              y = Math.sin(x * spatialFreq * 1.3 + speed * 0.9 + i * 0.7) * amp + Math.cos(x * spatialFreq * 2.6 + speed * 0.65 + i * 0.35) * (amp * 0.3);
            } else if (freq >= 639) {
              // Ethereal, complex wave — higher consciousness
              const amp = baseAmp + i * 16;
              y = Math.sin(x * spatialFreq * 1.0 + speed * 0.75 + i * 0.6) * amp + Math.cos(x * spatialFreq * 2.3 + speed * 0.9 + i * 0.45) * (amp * 0.35) + Math.sin(x * spatialFreq * 3.5 + speed * 0.5 + i * 0.3) * (amp * 0.12);
            } else {
              // Default smooth wave
              const amp = baseAmp + i * 15;
              y = Math.sin(x * spatialFreq + speed * 0.8 + i * 0.6) * amp;
            }
            if (x === 0) {
              ctx.moveTo(x, centerY + y);
            } else {
              ctx.lineTo(x, centerY + y);
            }
          }
        }
        ctx.strokeStyle = `rgba(${waveColorRGB}, ${0.7 - i * 0.18})`;
        ctx.lineWidth = 2.5 - i * 0.3;
        ctx.stroke();
      }
    };
    const animate = () => {
      if (!ctx || !canvas) return;
      // Clear using display dimensions
      ctx.clearRect(0, 0, displayWidth, displayHeight);
      const gyroInfluence = gyroTilt / 45 * 50;
      const freqArray = frequencies && frequencies.length > 0 ? frequencies : [frequency];
      if (freqArray.length > 1) {
        // PREMIUM MODE: Two independent wave groups flowing in opposite directions
        drawWaveGroup(displayHeight * 0.35, timeRef.current, 1, gyroInfluence, colorRGB, freqArray[0]);
        drawWaveGroup(displayHeight * 0.65, -timeRef.current * 1.2, 2, gyroInfluence, secondaryColorRGB, freqArray[1]);
      } else {
        // STANDARD MODE: Single wave centered
        drawWaveGroup(displayHeight / 2, timeRef.current, 0, gyroInfluence, colorRGB, freqArray[0]);
      }
      // Small, consistent time increment for smooth animation
      timeRef.current += 0.04;
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [frequency, frequencies, colorRGB, secondaryColorRGB, isActive, gyroTilt]);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-0 pointer-events-none opacity-80" data-id="element-952" />;
}